/**
 * 用户留存 adapter（ADM-MTR-S015）：GET /api/v1/metrics/retention-cohorts 的 snake_case DTO →
 * 页面消费的 camelCase 行，并派生每格留存率（retained ÷ 同格 cohort_size，分母取同批聚合行）。
 *
 * 汇总行的明细（语言 / 地区）由各 cohort 子行按维度合计：人数直接相加，每格只合计已就绪（status=null）
 * 的格，与服务端 summary.cells 同口径。任一 cohort 的子行被截断（只返回前 100 个）时不合计：
 * 截掉的维度在各行不同，合出来的数会偏小且看不出来。
 */

import type {
  RetentionCohortCell,
  RetentionCohortCellStatus,
  RetentionCohortData,
  RetentionCohortGranularity,
  RetentionCohortGroupDimension,
  RetentionCohortSplitDimension,
  RetentionCohortUserType,
} from '@novel-admin/shared';

export type RetentionCellView = {
  period: number;
  retained: number | null;
  cohortSize: number | null;
  /** retained ÷ cohortSize；未就绪或分母为 0 → null */
  rate: number | null;
  status: RetentionCohortCellStatus;
};

export type RetentionChildView = {
  key: string;
  dim: string | null;
  dimensions: Partial<Record<RetentionCohortGroupDimension, string | null>>;
  cohortUsers: number;
  cells: RetentionCellView[];
};

export type RetentionRowView = {
  key: string;
  cohortStart: string;
  cohortEnd: string;
  cohortComplete: boolean;
  cohortUsers: number;
  cells: RetentionCellView[];
  children: RetentionChildView[];
  childrenTruncated: boolean;
};

export type RetentionCohortViewData = {
  window: { from: string; to: string };
  granularity: RetentionCohortGranularity;
  periods: number[];
  lastCompleteDay: string;
  userType: RetentionCohortUserType;
  groupBy: RetentionCohortGroupDimension[];
  splitBy: RetentionCohortSplitDimension | null;
  summary: RetentionRowView;
  rows: RetentionRowView[];
  options: RetentionCohortData['options'];
  generatedAt: string;
};

function toCell(cell: RetentionCohortCell): RetentionCellView {
  return {
    period: cell.period,
    retained: cell.retained,
    cohortSize: cell.cohort_size,
    rate:
      cell.status === null && cell.retained !== null && cell.cohort_size
        ? cell.retained / cell.cohort_size
        : null,
    status: cell.status,
  };
}

/** 跨 cohort 合计同一维度的子行：就绪格相加；全无就绪格时沿用「有成熟格 → 无数据，否则未到期」 */
function sumChildren(rows: RetentionRowView[], periods: number[]): RetentionChildView[] {
  const byDim = new Map<
    string,
    {
      dim: string | null;
      dimensions: RetentionChildView['dimensions'];
      users: number;
      cells: RetentionCellView[][];
    }
  >();
  for (const row of rows) {
    for (const child of row.children) {
      const entry = byDim.get(child.key) ?? {
        dim: child.dim,
        dimensions: child.dimensions,
        users: 0,
        cells: [],
      };
      entry.users += child.cohortUsers;
      entry.cells.push(child.cells);
      byDim.set(child.key, entry);
    }
  }
  return [...byDim.entries()]
    .map(([key, entry]) => ({
      key,
      dim: entry.dim,
      dimensions: entry.dimensions,
      cohortUsers: entry.users,
      cells: periods.map((period, index) => {
        const column = entry.cells.map((cells) => cells[index]);
        const ready = column.filter((cell) => cell?.status === null);
        if (!ready.length) {
          const anyMatured = column.some((cell) => cell && cell.status !== 'PENDING_MATURITY');
          return toCell({
            period,
            retained: null,
            cohort_size: null,
            status: anyMatured ? 'PENDING_INSTRUMENTATION' : 'PENDING_MATURITY',
          });
        }
        return toCell({
          period,
          retained: ready.reduce((sum, cell) => sum + (cell?.retained ?? 0), 0),
          cohort_size: ready.reduce((sum, cell) => sum + (cell?.cohortSize ?? 0), 0),
          status: null,
        });
      }),
    }))
    .sort((left, right) => right.cohortUsers - left.cohortUsers);
}

export function adaptMetricsRetentionCohorts(dto: RetentionCohortData): RetentionCohortViewData {
  const rows: RetentionRowView[] = dto.rows.map((row) => ({
    key: row.cohort_start,
    cohortStart: row.cohort_start,
    cohortEnd: row.cohort_end,
    cohortComplete: row.cohort_complete,
    cohortUsers: row.cohort_users,
    cells: row.cells.map(toCell),
    // 维度值是自由文本，JSON 序列化才能保证 key 唯一且 null 不与字面值相撞
    children: (row.children ?? []).map((child) => ({
      key: JSON.stringify(child.dims),
      dim: child.dim,
      dimensions: child.dims,
      cohortUsers: child.cohort_users,
      cells: child.cells.map(toCell),
    })),
    childrenTruncated: row.children_truncated ?? false,
  }));
  return {
    window: { from: dto.window.from, to: dto.window.to },
    granularity: dto.granularity,
    periods: dto.periods,
    lastCompleteDay: dto.last_complete_day,
    userType: dto.user_type,
    groupBy: dto.group_by,
    splitBy: dto.split_by,
    summary: {
      key: 'summary',
      cohortStart: dto.window.from,
      cohortEnd: dto.window.to,
      cohortComplete: true,
      cohortUsers: dto.summary.cohort_users,
      cells: dto.summary.cells.map(toCell),
      children:
        dto.group_by.length > 0 && !rows.some((row) => row.childrenTruncated)
          ? sumChildren(rows, dto.periods)
          : [],
      childrenTruncated: rows.some((row) => row.childrenTruncated),
    },
    rows,
    options: dto.options,
    generatedAt: dto.generated_at,
  };
}
